import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import {
  listReviewRequests,
  createReviewRequest,
  deleteReviewRequest,
  sendReviewEmailFn,
  markReviewCompleted,
  stopReviewEmails,
  getReviewSettings,
  updateReviewSettings,
} from "@/lib/reviews.functions";

export const Route = createFileRoute("/admin/ertekelesek")({
  head: () => ({
    meta: [
      { title: "Értékeléskezelő — LAKROSZ admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminReviewsPage,
});

type Row = {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  license_plate: string | null;
  car_type: string | null;
  service_type: string;
  handover_date: string;
  staff_name: string | null;
  note: string | null;
  consent_given: boolean;
  customer_was_satisfied: boolean;
  status: string;
  first_email_sent_at: string | null;
  followup_email_sent_at: string | null;
  last_error: string | null;
  created_at: string;
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "Rögzítve", color: "bg-muted text-foreground" },
  first_sent: { label: "1. email elküldve", color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  followup_sent: { label: "Emlékeztető elküldve", color: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
  completed: { label: "Lezárva ✓", color: "bg-green-500/15 text-green-300 border-green-500/30" },
  no_consent: { label: "Nincs hozzájárulás", color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  error: { label: "Hiba", color: "bg-red-500/15 text-red-300 border-red-500/30" },
};

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" });
}

function AdminReviewsPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const list = useServerFn(listReviewRequests);
  const create = useServerFn(createReviewRequest);
  const del = useServerFn(deleteReviewRequest);
  const send = useServerFn(sendReviewEmailFn);
  const complete = useServerFn(markReviewCompleted);
  const stop = useServerFn(stopReviewEmails);
  const getSettings = useServerFn(getReviewSettings);
  const setSettings = useServerFn(updateReviewSettings);

  // Auth gate
  useEffect(() => {
    let unsub: (() => void) | undefined;
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate({ to: "/admin/login" });
        return;
      }
      setAuthorized(true);
      setAuthChecked(true);
    });
    const sub = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) navigate({ to: "/admin/login" });
    });
    unsub = () => sub.data.subscription.unsubscribe();
    return () => unsub?.();
  }, [navigate]);

  // Load data
  useEffect(() => {
    if (!authorized) return;
    setLoading(true);
    list()
      .then((r) => setRows(r.items as Row[]))
      .catch((e) => setToast({ kind: "err", msg: e.message }))
      .finally(() => setLoading(false));
  }, [authorized, reloadKey, list]);

  function showToast(kind: "ok" | "err", msg: string) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  }

  async function action<T>(id: string, label: string, fn: () => Promise<T>) {
    setBusy(id + label);
    try {
      await fn();
      setReloadKey((k) => k + 1);
      showToast("ok", label + " — kész");
    } catch (e) {
      showToast("err", e instanceof Error ? e.message : "Hiba");
    } finally {
      setBusy(null);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        r.customer_name.toLowerCase().includes(q) ||
        r.customer_email.toLowerCase().includes(q) ||
        (r.customer_phone || "").toLowerCase().includes(q) ||
        (r.license_plate || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      first: rows.filter((r) => r.first_email_sent_at).length,
      follow: rows.filter((r) => r.followup_email_sent_at).length,
      done: rows.filter((r) => r.status === "completed").length,
    };
  }, [rows]);

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Betöltés…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-surface sticky top-0 z-30 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-lg tracking-wider">
              LAKROSZ<span className="text-primary">.</span>
            </Link>
            <span className="hidden md:inline text-xs uppercase tracking-widest text-muted-foreground">
              Értékeléskezelő admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSettings(true)}
              className="text-sm px-3 py-2 rounded-full border border-border hover:border-primary"
            >
              ⚙ Beállítások
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                navigate({ to: "/admin/login" });
              }}
              className="text-sm px-3 py-2 rounded-full border border-border hover:border-destructive hover:text-destructive"
            >
              Kilépés
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Összes ügyfél" value={stats.total} />
          <StatCard label="Első email" value={stats.first} />
          <StatCard label="Emlékeztető" value={stats.follow} />
          <StatCard label="Lezárt" value={stats.done} accent />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <input
            placeholder="Keresés: név, email, telefon, rendszám..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-surface border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-surface border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
          >
            <option value="all">Minden státusz</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowForm(true)}
            className="px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold whitespace-nowrap"
          >
            + Új ügyfél
          </button>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Betöltés...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground border border-dashed border-border rounded-2xl">
            Nincs találat. Vegyél fel új ügyfelet a fenti gombbal.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <ReviewCard
                key={r.id}
                row={r}
                busy={busy}
                onSendFirst={() => action(r.id, "Első email", () => send({ data: { id: r.id, kind: "first" } }))}
                onSendFollow={() => action(r.id, "Emlékeztető", () => send({ data: { id: r.id, kind: "followup" } }))}
                onResend={() =>
                  action(r.id, "Újraküldés", () =>
                    send({ data: { id: r.id, kind: r.first_email_sent_at ? "followup" : "first" } }),
                  )
                }
                onComplete={() => action(r.id, "Lezárás", () => complete({ data: { id: r.id } }))}
                onStop={() => action(r.id, "Leállítás", () => stop({ data: { id: r.id } }))}
                onDelete={() => {
                  if (confirm(`Biztos törlöd: ${r.customer_name}?`)) {
                    action(r.id, "Törlés", () => del({ data: { id: r.id } }));
                  }
                }}
              />
            ))}
          </div>
        )}
      </main>

      {showForm && (
        <NewReviewModal
          onClose={() => setShowForm(false)}
          onSubmit={async (payload) => {
            try {
              await create({ data: payload });
              setShowForm(false);
              setReloadKey((k) => k + 1);
              showToast("ok", "Ügyfél rögzítve");
            } catch (e) {
              showToast("err", e instanceof Error ? e.message : "Hiba");
            }
          }}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          load={async () => (await getSettings()).google_review_link}
          save={async (v) => {
            await setSettings({ data: { google_review_link: v } });
            showToast("ok", "Beállítás mentve");
          }}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full shadow-xl border ${
            toast.kind === "ok"
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-destructive text-destructive-foreground border-destructive"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div
      className={`p-4 md:p-5 rounded-2xl border ${
        accent ? "bg-primary/10 border-primary/40" : "bg-surface border-border"
      }`}
    >
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 font-display text-3xl md:text-4xl ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function ReviewCard({
  row,
  busy,
  onSendFirst,
  onSendFollow,
  onResend,
  onComplete,
  onStop,
  onDelete,
}: {
  row: Row;
  busy: string | null;
  onSendFirst: () => void;
  onSendFollow: () => void;
  onResend: () => void;
  onComplete: () => void;
  onStop: () => void;
  onDelete: () => void;
}) {
  const s = STATUS_LABELS[row.status] || { label: row.status, color: "bg-muted" };
  const isBusy = (label: string) => busy === row.id + label;
  return (
    <div className="bg-surface border border-border rounded-2xl p-5 hover:border-primary/40 transition-colors">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="font-semibold text-lg">{row.customer_name}</div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${s.color}`}>
              {s.label}
            </span>
            {!row.consent_given && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-red-500/10 text-red-300 border border-red-500/30">
                ⚠ nincs hozzájárulás
              </span>
            )}
            {row.customer_was_satisfied && (
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-green-500/10 text-green-300 border border-green-500/30">
                ★ elégedett
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <div><span className="text-foreground/70">✉</span> {row.customer_email}</div>
            {row.customer_phone && <div>☎ {row.customer_phone}</div>}
            {row.license_plate && <div className="font-mono">🚗 {row.license_plate}</div>}
            {row.car_type && <div>{row.car_type}</div>}
            <div className="col-span-2 md:col-span-4">
              <span className="text-foreground">{row.service_type}</span> · átadás:{" "}
              {new Date(row.handover_date).toLocaleDateString("hu-HU")}
              {row.staff_name && ` · ${row.staff_name}`}
            </div>
            <div className="col-span-2 md:col-span-4 text-xs">
              1. email: {fmtDate(row.first_email_sent_at)} · emlékeztető: {fmtDate(row.followup_email_sent_at)}
            </div>
            {row.note && (
              <div className="col-span-2 md:col-span-4 text-xs italic text-foreground/60 mt-1">„{row.note}"</div>
            )}
            {row.last_error && (
              <div className="col-span-2 md:col-span-4 text-xs text-red-400 mt-1">⚠ {row.last_error}</div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end shrink-0">
          {!row.first_email_sent_at && row.consent_given && row.status !== "completed" && (
            <Btn primary disabled={isBusy("Első email")} onClick={onSendFirst}>
              ✉ Első email
            </Btn>
          )}
          {row.first_email_sent_at && !row.followup_email_sent_at && row.status !== "completed" && (
            <Btn disabled={isBusy("Emlékeztető")} onClick={onSendFollow}>
              ⏰ Emlékeztető
            </Btn>
          )}
          {row.first_email_sent_at && (
            <Btn disabled={isBusy("Újraküldés")} onClick={onResend}>
              ↻ Újraküldés
            </Btn>
          )}
          {row.status !== "completed" && (
            <>
              <Btn disabled={isBusy("Lezárás")} onClick={onComplete}>
                ✓ Lezárás értékelveként
              </Btn>
              <Btn disabled={isBusy("Leállítás")} onClick={onStop}>
                ⛔ Ne küldj több emailt
              </Btn>
            </>
          )}
          <Btn danger disabled={isBusy("Törlés")} onClick={onDelete}>
            🗑
          </Btn>
        </div>
      </div>
    </div>
  );
}

function Btn({
  children,
  onClick,
  primary,
  danger,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
  disabled?: boolean;
}) {
  const cls = primary
    ? "bg-primary text-primary-foreground border-primary"
    : danger
      ? "bg-transparent text-muted-foreground border-border hover:text-destructive hover:border-destructive"
      : "bg-background text-foreground border-border hover:border-primary";
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-wait ${cls}`}
    >
      {children}
    </button>
  );
}

function NewReviewModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    license_plate: "",
    car_type: "",
    service_type: "",
    handover_date: new Date().toISOString().slice(0, 10),
    staff_name: "",
    note: "",
    consent_given: false,
    customer_was_satisfied: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function update<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm({ ...form, [k]: v });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!form.consent_given) {
      setErr("A hozzájárulás kötelező — anélkül nem küldhetünk emailt.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        customer_phone: form.customer_phone || null,
        license_plate: form.license_plate || null,
        car_type: form.car_type || null,
        staff_name: form.staff_name || null,
        note: form.note || null,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Hiba");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 overflow-y-auto">
      <div className="bg-background border border-border rounded-t-2xl md:rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-5 flex items-center justify-between z-10">
          <h2 className="font-display text-2xl">Új ügyfél rögzítése</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
            ×
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Ügyfél neve *" required value={form.customer_name} onChange={(v) => update("customer_name", v)} />
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Email cím *"
              type="email"
              required
              value={form.customer_email}
              onChange={(v) => update("customer_email", v)}
            />
            <Field label="Telefonszám" value={form.customer_phone} onChange={(v) => update("customer_phone", v)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Rendszám" value={form.license_plate} onChange={(v) => update("license_plate", v)} />
            <Field label="Autó típusa" value={form.car_type} onChange={(v) => update("car_type", v)} />
          </div>
          <Field
            label="Szolgáltatás típusa *"
            required
            value={form.service_type}
            onChange={(v) => update("service_type", v)}
            placeholder="pl. Olajcsere, fékbetét csere"
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Átadás dátuma *"
              type="date"
              required
              value={form.handover_date}
              onChange={(v) => update("handover_date", v)}
            />
            <Field label="Munkatárs neve" value={form.staff_name} onChange={(v) => update("staff_name", v)} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Megjegyzés</label>
            <textarea
              rows={3}
              value={form.note}
              onChange={(e) => update("note", e.target.value)}
              className="mt-1 w-full bg-surface border border-border rounded-xl px-4 py-3 outline-none focus:border-primary resize-none"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-surface border border-border">
            <input
              type="checkbox"
              checked={form.consent_given}
              onChange={(e) => update("consent_given", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[oklch(0.92_0.20_105)]"
            />
            <span className="text-sm">
              <span className="font-medium">Az ügyfél hozzájárult az utólagos kapcsolatfelvételhez *</span>
              <br />
              <span className="text-xs text-muted-foreground">
                Az adatokat kizárólag a szervizelést követő kapcsolattartás és elégedettségmérés céljából kezeljük.
              </span>
            </span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-surface border border-border">
            <input
              type="checkbox"
              checked={form.customer_was_satisfied}
              onChange={(e) => update("customer_was_satisfied", e.target.checked)}
              className="w-4 h-4 accent-[oklch(0.92_0.20_105)]"
            />
            <span className="text-sm">Az ügyfél elégedetten vette át az autót</span>
          </label>

          {err && <div className="text-sm text-destructive">{err}</div>}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-full border border-border hover:border-foreground"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-3 rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50"
            >
              {submitting ? "Mentés..." : "Rögzítés"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-surface border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
      />
    </div>
  );
}

function SettingsModal({
  onClose,
  load,
  save,
}: {
  onClose: () => void;
  load: () => Promise<string>;
  save: (v: string) => Promise<void>;
}) {
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    load()
      .then(setLink)
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [load]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      await save(link);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Hiba");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl w-full max-w-lg">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-2xl">Beállítások</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
            ×
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">
              Google értékelés link
            </label>
            <input
              type="url"
              required
              disabled={loading}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://g.page/r/.../review"
              className="mt-1 w-full bg-surface border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
            />
            <p className="mt-2 text-xs text-muted-foreground">
              Ezt a linket kapják meg az ügyfelek az értékeléskérő emailben.
            </p>
          </div>
          {err && <div className="text-sm text-destructive">{err}</div>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-full border border-border"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={saving || loading}
              className="flex-1 py-3 rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50"
            >
              {saving ? "Mentés..." : "Mentés"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
